// Curated catalogue of car makes and their most-rented models for the European market.
// Used by the vendor "Add car" form to power two cascading dropdowns: pick a Make →
// model list filters to that make's catalogue. An "Other" escape hatch in both selects
// lets vendors free-type a rare make/model so the picklist never blocks a real listing.
//
// Scope choice: ~35 makes × 8–14 models each = ~360 entries covers >95% of the EU rental
// fleet without the catalogue becoming unwieldy. Source: blend of ACEA + ANFIA most-sold
// 2024 + the actual fleet most rental brands publish (Sixt, Europcar, Hertz EU). Order
// within each make is rough popularity-desc so the most common Model 3 / Golf / 3 Series
// lands near the top of the dropdown.
//
// Add/edit safely: this file has no imports — every consumer reads `CAR_MAKES` (sorted
// alphabetic) and `CAR_MODELS[make]` (already in display order).

export const CAR_MODELS = {
  Audi: ["A1", "A3", "A4", "A5", "A6", "A7", "A8", "Q2", "Q3", "Q5", "Q7", "Q8", "e-tron", "RS6"],
  BMW: ["1 Series", "2 Series", "3 Series", "4 Series", "5 Series", "7 Series", "X1", "X3", "X5", "X6", "X7", "i4", "iX", "M3"],
  "Mercedes-Benz": ["A-Class", "B-Class", "C-Class", "E-Class", "S-Class", "CLA", "CLS", "GLA", "GLB", "GLC", "GLE", "GLS", "EQA", "EQS"],
  Volkswagen: ["Polo", "Golf", "Passat", "Tiguan", "Touran", "T-Roc", "Arteon", "ID.3", "ID.4", "ID.5", "Touareg", "Up!"],
  Volvo: ["S60", "S90", "V60", "V90", "XC40", "XC60", "XC90", "EX30", "EX90"],
  Toyota: ["Aygo", "Yaris", "Corolla", "C-HR", "RAV4", "Camry", "Prius", "Land Cruiser", "Hilux", "Highlander", "bZ4X"],
  Honda: ["Jazz", "Civic", "HR-V", "CR-V", "e:Ny1"],
  Nissan: ["Micra", "Juke", "Qashqai", "X-Trail", "Leaf", "Ariya", "Note"],
  Hyundai: ["i10", "i20", "i30", "Kona", "Tucson", "Santa Fe", "Ioniq 5", "Ioniq 6"],
  Kia: ["Picanto", "Rio", "Ceed", "XCeed", "Stonic", "Sportage", "Sorento", "EV6", "EV9", "Niro"],
  Renault: ["Twingo", "Clio", "Captur", "Mégane", "Arkana", "Kadjar", "Austral", "Espace", "Zoe", "Scenic E-Tech"],
  Peugeot: ["108", "208", "2008", "308", "3008", "5008", "408", "508", "e-208", "Rifter"],
  Citroën: ["C1", "C3", "C4", "C4 X", "C5 Aircross", "C5 X", "Berlingo", "ë-C4"],
  Opel: ["Corsa", "Astra", "Mokka", "Crossland", "Grandland", "Insignia", "Combo"],
  Ford: ["Fiesta", "Focus", "Puma", "Kuga", "EcoSport", "Mondeo", "Mustang", "Mustang Mach-E", "Galaxy", "S-Max", "Transit"],
  Tesla: ["Model 3", "Model Y", "Model S", "Model X", "Cybertruck"],
  Fiat: ["500", "500e", "500X", "Panda", "Tipo", "600e"],
  Alfa: ["Giulia", "Stelvio", "Tonale", "Giulietta"],
  Lancia: ["Ypsilon"],
  Lamborghini: ["Huracán", "Urus", "Revuelto"],
  Ferrari: ["Roma", "Portofino M", "296 GTB", "SF90 Stradale"],
  Maserati: ["Ghibli", "Levante", "Grecale", "Quattroporte", "MC20"],
  Porsche: ["911", "718 Cayman", "718 Boxster", "Macan", "Cayenne", "Panamera", "Taycan"],
  Mini: ["Cooper", "Cooper S", "Countryman", "Clubman", "Cooper SE", "Aceman"],
  Skoda: ["Fabia", "Scala", "Octavia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Enyaq"],
  Seat: ["Ibiza", "Leon", "Arona", "Ateca", "Tarraco"],
  Cupra: ["Born", "Formentor", "Leon", "Ateca", "Tavascan"],
  "Land Rover": ["Defender", "Discovery", "Discovery Sport", "Range Rover", "Range Rover Sport", "Range Rover Evoque", "Range Rover Velar"],
  Jaguar: ["F-Pace", "E-Pace", "I-Pace", "XE", "XF"],
  Jeep: ["Renegade", "Compass", "Wrangler", "Grand Cherokee", "Avenger"],
  Mazda: ["2", "3", "6", "CX-3", "CX-30", "CX-5", "CX-60", "MX-5", "MX-30"],
  Subaru: ["Impreza", "XV", "Forester", "Outback", "Solterra"],
  Suzuki: ["Swift", "Ignis", "Vitara", "S-Cross", "Jimny", "Across"],
  Mitsubishi: ["Space Star", "ASX", "Eclipse Cross", "Outlander", "L200"],
  DS: ["DS 3", "DS 4", "DS 7", "DS 9"],
  Bentley: ["Continental GT", "Flying Spur", "Bentayga"],
  "Rolls-Royce": ["Ghost", "Phantom", "Cullinan", "Spectre"],
  "Aston Martin": ["DB12", "Vantage", "DBX"],
  McLaren: ["GT", "Artura", "750S"],
  BYD: ["Atto 3", "Han", "Seal", "Dolphin", "Tang"],
  MG: ["MG3", "MG4", "MG5", "ZS", "HS", "Marvel R"],
  Polestar: ["2", "3", "4"],
  Lexus: ["UX", "NX", "RX", "RZ", "LBX", "ES", "LS"],
  Genesis: ["G70", "G80", "GV60", "GV70", "GV80"],
  Smart: ["#1", "#3", "ForTwo", "ForFour"],
};

export const CAR_MAKES = Object.keys(CAR_MODELS).sort((a, b) => a.localeCompare(b));

export const OTHER_OPTION = "__other__";
